import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { setUser as setSentryUser } from "@sentry/vue";

// Profile returned by AuthUtils.getProfile; nullable fields follow the user model.
// To be removed once we introduce proper BE / FE contracts
export interface UserProfile {
  cuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phonePrefix: string | null;
  phoneNumber: string | null;
  blocked: boolean;
  role: string;
  dateFormat: string | null;
  numberFormat: string | null;
  timeFormat: string | null;
  timeZone: string | null;
  limit: number | null;
  language: string | null;
  showTour: boolean;
  termsAccepted: boolean;
  position: string | null;
  features: string[];
  csrfToken?: string;
  company: {
    cuid: string;
    name: string;
    type: number;
    demo: boolean;
    jurisdiction: string;
    licenses: { product: string; plan: string }[];
    hasPlatformAccess: boolean;
  } | null;
}

export const useUserStore = defineStore("user", () => {
  const user = ref<UserProfile | null>(null);
  const language = ref<UserProfile["language"]>("en");
  const isPerTonneView = ref(true);

  const setUser = (data: UserProfile) => {
    user.value = data;
    language.value = data.language;
    setSentryUser(data.cuid ? { id: data.cuid } : null);
  };
  const isCompanyType = (type: number) => user.value?.company?.type === type;
  const isCompanyJurisdiction = (jurisdiction: string) => user.value?.company?.jurisdiction === jurisdiction;
  const hasProductAccess = (...products: string[]) => {
    const company = user.value?.company;
    const licenses = company?.licenses || [];
    return licenses.some(
      (license) => license.plan !== "FREE" && (!products.length || products.includes(license.product)),
    );
  };
  const showTour = ref(true);
  const isLoggedIn = computed(() => !!user.value);
  const csrfToken = computed(() => user.value?.csrfToken);

  return {
    user,
    language,
    csrfToken,
    setUser,
    isLoggedIn,
    isCompanyType,
    isCompanyJurisdiction,
    hasProductAccess,
    showTour,
    isPerTonneView,
  };
});
